import { CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import { lessonAPI } from '@api'
import { EUploadFileType } from '@configs'
import { useMediaQuery } from '@utils'
import { message, Upload, UploadProps } from 'antd'
import { RcFile } from 'antd/es/upload'
import { AxiosRequestConfig } from 'axios'
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { galleryAPI } from 'src/api/gallery'
import { EMediaCategory } from 'src/interfaces/gallery'
import {
  IUploadMediaResponse
} from 'src/interfaces/media'
import { Uploader } from 'src/utils/multipartUpload'
import { SimpleImage } from '../image/SimpleImage'
import { Text } from '../typography'
import { toast } from 'react-toastify'

export const CustomDragger = (
  props: UploadProps & {
    containerClassName?: string
    label?: string
    placeholder?: string
    required?: boolean
    onLoadEnd?: (data: IUploadMediaResponse) => void
    onSetDuration?: (duration: number) => void
    errors?: string
    initResource?: string
    reset?: boolean
    changeLoading?: (loading: boolean) => void
    allowFileTypes?: string[]
    limitFileSize?: number
    minFileSize?: number
    uploadType?: EUploadFileType
    uploadCategory?: EMediaCategory
    labelClassName?: string
    alignment?: 'row' | 'col'
    note?: string
    aborted?: boolean
    handleDeleteMedia?: (type: string) => void
  }
) => {
  const {
    containerClassName,
    required,
    label,
    placeholder = 'Click to upload media file',
    onLoadEnd,
    initResource,
    errors,
    reset,
    changeLoading,
    allowFileTypes = ['image/png', 'image/jpeg', 'image/jpg'],
    limitFileSize = 20,
    minFileSize = 0,
    uploadType = EUploadFileType.IMAGE,
    uploadCategory,
    name = 'avatar',
    id = 'avatar',
    labelClassName,
    alignment = 'row',
    disabled,
    note,
    aborted = false,
    onSetDuration,
    handleDeleteMedia,
    ...restProps
  } = props
  const [loading, setLoading] = useState(false)
  const [resource, setResource] = useState<string | undefined>(initResource)
  const isSMScreen = useMediaQuery(`(max-width:640px)`)
  const [multipartFile, setMultipartFile] = useState<File>()
  const [uploader, setUploader] = useState<Uploader>()
  const [duration, setDuration] = useState(null)
  const [isDone, setIsDone] = useState(false)
  const videoRef = React.createRef<any>()
  const audioRef = React.createRef<any>()
  const inputVideoRef = React.createRef<any>()
  const { t } = useTranslation(['common', 'error'])

  const handleLoadedMetadata = () => {
    setDuration(videoRef?.current?.duration || 0)
    onSetDuration && onSetDuration(videoRef?.current?.duration || 0)
  }
  const handleLoadedMetadataAudio = () => {
    setDuration(audioRef?.current?.duration || 0)
    onSetDuration && onSetDuration(audioRef?.current?.duration || 0)
  }
  let inputContainerClass = 'grid grid-cols-7 gap-3'
  let labelClass = 'text-right mb-0 '
  let localAlignment = isSMScreen ? 'col' : alignment

  if (localAlignment === 'col') {
    inputContainerClass = 'flex items-start flex-col '
    labelClass = 'text-left mb-2 '
  }

  const validateFileMedia = useCallback(
    (file: File) => {
      const isMatchMediaType = allowFileTypes.includes(file.type)

      const allowedInputType = allowFileTypes
        ?.map((item, index) => item.split('/')[1])
        ?.join('/')
        ?.toUpperCase()

      if (!isMatchMediaType) {
        toast.error(`You can only upload ${allowedInputType}file!`)
        setLoading(false)
        changeLoading?.(false)
        inputVideoRef.current.value = null
        return false
      }

      const isMatchMediaSize =
        limitFileSize > 0
          ? Number(file.size) <= limitFileSize * 1024 * 1024
          : true

      if (!isMatchMediaSize) {
        toast.error(`Media file must smaller than ${limitFileSize}MB!`)
        setLoading(false)
        changeLoading?.(false)
        inputVideoRef.current.value = null
        return false
      }

      const isSmallerThanMinSize =
        minFileSize > 0 ? Number(file.size) < minFileSize * 1024 * 1024 : false

      if (isSmallerThanMinSize) {
        toast.error(
          t('error:file_size_too_small', {
            fileSize: minFileSize,
          })
        )
        setLoading(false)
        changeLoading?.(false)
        inputVideoRef.current.value = null
        return false
      }

      return true
    },
    [
      allowFileTypes,
      changeLoading,
      inputVideoRef,
      limitFileSize,
      minFileSize,
      t,
    ]
  )

  const handleChangeMedia = async (e: ChangeEvent<HTMLInputElement>) => {
    setLoading(true)
    changeLoading?.(true)
    if (!e.target.files || e.target.files.length === 0) {
      setLoading(false)
      changeLoading?.(false)
      return
    }

    const file = e.target.files[0]

    if (!validateFileMedia(file)) return
    try {
      if (uploadType === EUploadFileType.IMAGE) {
        const response = await galleryAPI.uploadImage(file, uploadCategory)
        if (response.success) {
          if (onLoadEnd) {
            setResource(response?.data.original)
            onLoadEnd(response?.data)
          }
          setLoading(false)
          changeLoading?.(false)
        }
      } else {
        setMultipartFile(file)

        const videoUploaderOptions = {
          fileName: file.name,
          fileType: file.type,
          file: file,
        }

        const uploader = new Uploader(videoUploaderOptions)
        setUploader(uploader)

      }
    } catch (error: any) {
      setLoading(false)
      toast.error(
        error?.code === 500
          ? t('common:upload_file_failed')
          : error?.message ?? t('common:upload_file_failed')
      )
    }
  }

  const handleStartUpload = async () => {
    let percentage: number

    if (!uploader || !multipartFile) return
    await uploader.start()
    uploader.onProgressFn((data: any) => {
      if (data.percentage !== percentage) {
        percentage = data.percentage
      }
    })

    uploader.onErrorFn((error: any) => {
      setMultipartFile(undefined)
      console.error(error)
    })

    uploader.setUploadCompletionCallback(async () => {
      try {
        changeLoading?.(true)
        const result = await uploader.sendCompleteRequest()
        if (result.Location) {
          const formData = new FormData()
          // formData.append('file', multipartFile)
          formData.append('bucket', result.Bucket)
          formData.append('key', result.Key)
          formData.append('contentType', multipartFile.type)
          formData.append('originalname', multipartFile.name)
          if (uploadCategory) {
            formData.append('category', uploadCategory)
          }
          createMedia(formData)
        }
      } catch (error: any) {
        console.log(error)
        changeLoading?.(false)
        toast.error(t('error:upload_failed'))
      } finally {
        changeLoading?.(true)
        setLoading(false)
      }
    })
  }

  const createMedia = async (data: any) => {
    try {
      const result = await galleryAPI.createMedia(data)
      if (result) {
        if (onLoadEnd) {
          onLoadEnd(result?.data)
        }
        setResource(result.data.url)
      }
    } catch (error) {
      toast.error(t('error:upload_failed'))
    } finally {
      setIsDone(true)
    }
  }

  useEffect(() => {
    handleStartUpload()
  }, [multipartFile, uploader])

  useEffect(() => {
    if (duration !== null && duration > 0 && isDone) {
      message.success(t('common:upload_success'))
      setLoading(false)
      changeLoading?.(false)
      setIsDone(false)
    }
  }, [isDone, duration])

  const customRequest: UploadProps['customRequest'] = async (options) => {
    const { onSuccess, onProgress } = options
    setLoading(true)

    const file = options.file as RcFile

    const config: AxiosRequestConfig<any> = {
      headers: { 'content-type': 'multipart/form-data' },
    }

    try {
      const res = await lessonAPI.uploadMedia(file, config)
      if (onSuccess) {
        onSuccess(res.data)
      }
      if (onLoadEnd) {
        onLoadEnd(res?.data?.dataValues as any)
      }
      setResource(res.data?.url || '')
      message.success(res.data?.message)
    } catch (err) {
      console.error('server error: ', err)
    } finally {
      setLoading(false)
    }
  }
  const handleChange: UploadProps['onChange'] = (info) => {
    let newFileList = [...info.fileList]
    newFileList = newFileList.map((file) => {
      if (file.response) {
        // Component will show file.url as link
        file.url = file.response.url
      }
      return file
    })
  }

  useEffect(() => {
    setResource(initResource)
  }, [reset, initResource, uploadType])

  useEffect(() => {
    if (!resource) {
      inputVideoRef.current.value = null
    }
  }, [resource])

  useEffect(() => {
    if (aborted && uploader) {
      uploader.aborted = false
    }
  }, [aborted, uploader])

  return (
    <div key={id} className="w-full">
      <div
        className={`Input w-full ${inputContainerClass} ${containerClassName || ''
          }`}
      >
        {label && (
          <label
            htmlFor={label}
            className={`Input__label ${labelClass} inline-flex mr-[1.25rem] capitalize  text-dark col-span-2 `}
          >
            {label}
            {required && (
              <span className="required text-[#B91C1C] font-bold"> *</span>
            )}
          </label>
        )}

        <div
          className={`Input__field-container ${labelClassName} relative ${label ? 'col-span-5' : 'col-span-7'
            }`}
        >
          <label
            className={`icon-change-avatar cursor-pointer border-[1.5px] border-dashed h-[200px] w-full p-5 col-span-5 inline-flex items-center justify-center rounded-lg ${disabled ? '!cursor-not-allowed' : 'hover:border-primary'
              } `}
            htmlFor={name}
            style={{
              border: errors ? '1px solid #B91C1C' : '',
              borderRadius: errors ? '0.375rem' : '',
            }}
          >
            {resource && handleDeleteMedia && <div
              className="flex items-center justify-center absolute right-2 top-2 cursor-pointer z-30 bg-white rounded-full"
              onClick={(e) => {
                e.preventDefault()
                handleDeleteMedia(id)
              }}
            >
              <CloseCircleOutlined className=" text-[20px]" />
            </div>}
            {uploadType !== EUploadFileType.AUDIO ? (
              loading ? (
                <LoadingOutlined size={32} color={'blue'} spin />
              ) : resource ? (
                uploadType === EUploadFileType.IMAGE ? (
                  <SimpleImage source={resource} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <video
                      src={resource}
                      loop
                      ref={videoRef}
                      onLoadedMetadata={handleLoadedMetadata}
                      controls={!!resource}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                      }}
                    />
                  </div>
                )
              ) : (
                <Text>Click to upload file!</Text>
              )
            ) : (
              <></>
            )}
            {uploadType === EUploadFileType.AUDIO && (
              <Upload
                accept=".mp3"
                onChange={handleChange}
                id={id}
                name={name}
                showUploadList={false}
                customRequest={customRequest}
              >
                {loading ? (
                  <LoadingOutlined size={32} color={'blue'} spin />
                ) : resource ? (
                  <audio
                    controls
                    src={resource}
                    ref={audioRef}
                    onLoadedMetadata={handleLoadedMetadataAudio}
                  />
                ) : (
                  <Text>Click to upload file!</Text>
                )}
              </Upload>
            )}
          </label>

          {uploadType !== EUploadFileType.AUDIO && (
            <input
              ref={inputVideoRef}
              type="file"
              id={id}
              name={name}
              className="hidden w-3/4"
              accept={allowFileTypes.join(',')}
              onChange={handleChangeMedia}
              disabled={disabled}
            />
          )}
        </div>
      </div>
      {errors && (
        <div className="grid grid-cols-7 w-full ">
          {label && localAlignment === 'row' && (
            <div className={labelClass + ' col-span-2 min-w-[1px]'}></div>
          )}
          <div
            style={{
              color: 'rgb(var(--color-danger)',
            }}
            className={`Input__text-error mt-2 text-sm col-span-7 ${alignment === 'col' ? 'sm:col-span-7 text-left' : 'sm:col-span-5'
              }`}
          >
            {errors}
          </div>
        </div>
      )}
    </div>
  )
}
